import { CSSProperties, FormEvent, useEffect, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import * as pdfjsLib from 'pdfjs-dist';
import pdfWorker from 'pdfjs-dist/build/pdf.worker.min.mjs?worker&url';
import { Marked, type Tokens } from 'marked';
import katex from 'katex';
import type { LinkLookupResponse } from '@cloudbtl/shared';
import { api, ApiError } from '../api/client.js';
import { loadAccessToken, saveAccessToken } from '../lib/visitor.js';
import { RoomTracker } from '../lib/tracker.js';
import { useDocumentMeta } from '../lib/document-meta.js';
import { GoogleButton, GOOGLE_CLIENT_ID } from '../components/GoogleButton.js';
import { BrandingShell } from '../components/BrandingShell.js';
import { useT } from '../state/i18n.js';

// 브라우저 미리보기가 불가능한 문서 포맷 — 서버 filenames.ts 의 DocumentType 과 짝을 맞춘다.
const NO_PREVIEW_TYPES = new Set(['pptx', 'docx', 'xlsx', 'file']);

pdfjsLib.GlobalWorkerOptions.workerSrc = pdfWorker;

type GateState = {
  // 'email' = 수신자 이메일 캡처 게이트(검증 없음) — 서버 access.ts 의 AccessMode 와 동치.
  // shared 의 AccessMode 에는 아직 'email' 이 없어 여기서 로컬로 넓힌다 (shared 갱신은 교차 계약).
  mode: 'passcode' | 'org' | 'email';
  allowedDomains: string[];
  passcodeFallback: boolean;
};

// 추적 고지 스타일 — styles.css 는 이 작업 소유가 아니라 인라인으로 자기완결.
// 문서 렌더를 가리지 않는 것이 제약: .viewer 는 flex column 이므로 flex:0 0 auto 하단 바.
const TRACKING_NOTICE_STYLE: CSSProperties = {
  flex: '0 0 auto',
  padding: '6px 16px',
  fontSize: 12,
  lineHeight: 1.5,
  textAlign: 'center',
  color: '#9ca3af',
  background: 'rgba(16, 18, 22, 0.92)',
  borderTop: '1px solid rgba(255, 255, 255, 0.08)',
  zIndex: 10,
};

type Proposal = Extract<LinkLookupResponse, { ok: true }>['proposal'];

/**
 * Injected into the recipient's HTML (sandboxed iframe) to report read depth.
 * It divides the document height into 10 bands and reports the max scroll depth
 * reached plus dwell time per band, via postMessage to the parent Room. No
 * headings or markers required — purely height-based.
 */
const READ_TRACKER = `<script>(function(){try{
var BANDS=10,dwell=new Array(BANDS).fill(0),maxDepth=0,last=Date.now();
function docH(){return Math.max(document.body?document.body.scrollHeight:0,document.documentElement.scrollHeight,1);}
function tick(){var now=Date.now(),dt=now-last;last=now;var h=docH();var top=window.scrollY||document.documentElement.scrollTop||0;var vh=window.innerHeight||0;var depth=Math.max(0,Math.min(100,Math.round((top+vh)/h*100)));if(depth>maxDepth)maxDepth=depth;var band=Math.max(0,Math.min(BANDS-1,Math.floor((top+vh/2)/h*BANDS)));if(dt>0&&dt<15000&&document.visibilityState!=='hidden')dwell[band]+=dt;}
function send(){tick();try{parent.postMessage({__cloudbtl:1,type:'read_progress',maxDepthPct:maxDepth,bandMs:dwell.map(function(x){return Math.round(x);})},'*');}catch(e){}}
window.addEventListener('scroll',tick,{passive:true});
setInterval(send,4000);
document.addEventListener('visibilitychange',function(){if(document.visibilityState==='hidden')send();});
window.addEventListener('pagehide',send);
setTimeout(send,1500);
}catch(e){}})();</script>`;

// DeckLens SDK (섹션 수준 계측) — 정본은 cloudbtl/DeckLens 리포, 여기는 벤더 사본.
// 샌드박스 iframe 은 API 원점에 못 닿으므로 postMessage 로 부모(Room)에 중계,
// Room 의 RoomTracker 가 linkId 를 붙여 /api/events 로 전달한다.
import decklensSdkSource from '../lib/decklens.sdk.js?raw';

function decklensInjection(sessionId: string, visitorId: string): string {
  const init = JSON.stringify({ transport: 'postMessage', sessionId, visitorId });
  return (
    `<script>${decklensSdkSource}</script>` +
    `<script>try{window.DeckLens.createTracker(${init}).start();}catch(e){}</script>`
  );
}

// md 문서 스타일 — 샌드박스 srcdoc iframe 은 외부 리소스에 못 닿으므로 인라인으로 완결.
const MD_DOC_STYLE = `<style>
:root{color-scheme:light}
body{margin:0;background:#fff;color:#1f2328;font:16px/1.7 -apple-system,BlinkMacSystemFont,"Segoe UI","Noto Sans KR",sans-serif;-webkit-text-size-adjust:100%}
main{max-width:760px;margin:0 auto;padding:48px 20px 96px;overflow-wrap:break-word}
.math-block{margin:1.1em 0;overflow-x:auto;text-align:center}
math{font-size:1.05em}
h1,h2,h3,h4,h5,h6{line-height:1.3;margin:1.6em 0 .6em;font-weight:700}
h1{font-size:1.85em}
h2{font-size:1.45em;padding-bottom:.25em;border-bottom:1px solid #e7e9ec}
h3{font-size:1.2em}
h4,h5,h6{font-size:1em}
h1:first-child{margin-top:0}
p{margin:0 0 1em}
a{color:#175cd3}
img{max-width:100%;height:auto}
ul,ol{margin:0 0 1em;padding-left:1.6em}
li{margin:.25em 0}
blockquote{margin:1em 0;padding:.1em 1em;border-left:3px solid #d0d5dc;color:#57606a}
code{font-family:ui-monospace,SFMono-Regular,Menlo,Consolas,monospace;font-size:.88em;background:#f0f2f4;border-radius:4px;padding:.15em .4em}
pre{background:#0d1117;color:#e6edf3;border-radius:8px;padding:14px 16px;overflow-x:auto;margin:0 0 1.2em}
pre code{background:none;padding:0;font-size:.85em;color:inherit}
table{border-collapse:collapse;margin:0 0 1.2em;display:block;max-width:100%;overflow-x:auto}
th,td{border:1px solid #d8dde3;padding:.4em .7em;text-align:left}
th{background:#f6f8fa}
hr{border:0;border-top:1px solid #e7e9ec;margin:2em 0}
</style>`;

function escapeHtml(value: string): string {
  return value.replace(/[&<>"']/g, (c) =>
    c === '&' ? '&amp;' : c === '<' ? '&lt;' : c === '>' ? '&gt;' : c === '"' ? '&quot;' : '&#39;',
  );
}

// ── md 본문 소독 ─────────────────────────────────────────────────────────
// marked v5+ 는 sanitize 를 하지 않아 md 안의 raw HTML(<script>, onerror 속성 등)이
// srcdoc iframe 에서 그대로 실행된다. 업로더 본문은 신뢰할 수 없으므로 반드시
// 아래 mdParser(renderMdBody)로만 변환한다. 계측 스크립트(READ_TRACKER·
// decklensInjection)는 이 파서를 거치지 않고 wrapMdDocument 이후 </body> 치환으로
// 붙으므로, 소독 범위가 문서 본문뿐임은 renderMdBody 함수 경계로 보장된다.

// 브라우저는 href 속성값의 HTML 엔티티를 디코드하고 제어문자·공백을 무시한 뒤
// 스킴을 해석한다 — 같은 규칙으로 정규화한 문자열로 판정해야 javascript&#58; 류
// 엔티티 우회를 막는다 (marked 의 cleanUrl 은 & 를 이스케이프하지 않음).
function decodeHrefForSchemeCheck(href: string): string {
  return href
    .replace(/&#x([0-9a-f]+);?/gi, (_, hex: string) => {
      const cp = parseInt(hex, 16);
      return cp <= 0x10ffff ? String.fromCodePoint(cp) : '�';
    })
    .replace(/&#(\d+);?/g, (_, dec: string) => {
      const cp = parseInt(dec, 10);
      return cp <= 0x10ffff ? String.fromCodePoint(cp) : '�';
    })
    .replace(/&([a-z]+);/gi, (m, name: string) => {
      const n = name.toLowerCase();
      return n === 'colon'
        ? ':'
        : n === 'tab'
          ? '\t'
          : n === 'newline'
            ? '\n'
            : n === 'amp'
              ? '&'
              : m;
    })
    .replace(/[\u0000-\u0020]+/g, '');
}

// 스킴 화이트리스트: http/https/mailto(+이미지만 data:image) — javascript: 등은 # 로
// 무력화. 스킴 없는 상대 경로는 srcdoc 안에서 해석만 안 될 뿐 실행 표면이 아니라 통과.
function sanitizeMdUrl(href: string, isImage: boolean): string {
  const decoded = decodeHrefForSchemeCheck(href).toLowerCase();
  const scheme = /^([a-z][a-z0-9+.-]*):/.exec(decoded)?.[1];
  if (!scheme) return href;
  if (scheme === 'http' || scheme === 'https' || scheme === 'mailto') return href;
  if (isImage && decoded.startsWith('data:image/')) return href;
  return '#';
}

const mdParser = new Marked({
  async: false,
  walkTokens(token) {
    if (token.type === 'html') {
      // 렌더러의 html()은 text 를 무가공 출력하므로 여기서 이스케이프하면 블록·
      // 인라인 raw HTML 모두 문자 그대로만 남는다. 코드펜스는 code 토큰이라 무관 —
      // marked 기본 렌더러가 원래대로 이스케이프한다.
      token.text = escapeHtml(token.text);
    } else if (token.type === 'text' && token.escaped) {
      // <script>/<pre> raw 블록 내부 텍스트는 escaped 플래그로 무이스케이프 출력됨 — 해제
      token.escaped = false;
    } else if (token.type === 'link' || token.type === 'image') {
      token.href = sanitizeMdUrl(token.href, token.type === 'image');
    }
  },
});

// LaTeX 수식: KaTeX 를 MathML 출력 모드로 — 브라우저 네이티브 렌더라 폰트·CSS·외부리소스
// 불필요(샌드박스 srcdoc 에서도 동작). $$…$$ = 디스플레이, $…$ = 인라인. 잘못된 수식은
// throwOnError:false 로 원본을 빨간 텍스트로 남긴다(문서 전체가 깨지지 않게).
function renderTex(tex: string, displayMode: boolean): string {
  return katex.renderToString(tex, { output: 'mathml', throwOnError: false, displayMode });
}
mdParser.use({
  extensions: [
    {
      name: 'blockKatex',
      level: 'block',
      start(src: string) {
        const i = src.indexOf('$$');
        return i < 0 ? undefined : i;
      },
      tokenizer(src: string) {
        const m = /^\$\$([\s\S]+?)\$\$/.exec(src);
        if (m) return { type: 'blockKatex', raw: m[0], text: (m[1] ?? '').trim() };
        return undefined;
      },
      renderer(token: Tokens.Generic) {
        return `<div class="math-block">${renderTex(String(token.text ?? ''), true)}</div>`;
      },
    },
    {
      name: 'inlineKatex',
      level: 'inline',
      start(src: string) {
        const i = src.indexOf('$');
        return i < 0 ? undefined : i;
      },
      tokenizer(src: string) {
        // 통화($5, $10) 오탐 축소: 여는 $ 뒤 공백 금지 + 닫는 $ 뒤 숫자 금지.
        const m = /^\$([^\s$][^\n$]*?)\$(?!\d)/.exec(src);
        if (m) return { type: 'inlineKatex', raw: m[0], text: (m[1] ?? '').trim() };
        return undefined;
      },
      renderer(token: Tokens.Generic) {
        return renderTex(String(token.text ?? ''), false);
      },
    },
  ],
});

function renderMdBody(markdown: string): string {
  return mdParser.parse(markdown) as string;
}

// marked 산출 HTML 을 완성 문서로 래핑 — </body> 가 있어야 계측 주입 replace 가 동작한다.
function wrapMdDocument(title: string, bodyHtml: string): string {
  const safeTitle = escapeHtml(title);
  return (
    '<!doctype html><html><head><meta charset="utf-8">' +
    '<meta name="viewport" content="width=device-width, initial-scale=1">' +
    `<title>${safeTitle}</title>${MD_DOC_STYLE}</head>` +
    `<body><main>${bodyHtml}</main></body></html>`
  );
}

export function RoomPage(): JSX.Element {
  const t = useT();
  const { linkId = '' } = useParams<{ linkId: string }>();
  const [proposal, setProposal] = useState<Proposal | null>(null);
  const [gate, setGate] = useState<GateState | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [passcode, setPasscode] = useState('');
  const [gateEmail, setGateEmail] = useState('');
  const [currentPage, setCurrentPage] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [downloadUrl, setDownloadUrl] = useState<string | null>(null);
  const [frameHtml, setFrameHtml] = useState<string | null>(null);
  const pagesEl = useRef<HTMLDivElement>(null);
  const htmlFrame = useRef<HTMLIFrameElement>(null);
  // srcdoc iframe 에 실린 문서 종류 — read_progress 계측의 target 구분용
  const frameTarget = useRef<'html' | 'md'>('html');
  const tracker = useRef<RoomTracker | null>(null);

  useDocumentMeta({
    title: proposal?.title ?? t({ ko: '보호된 문서', en: 'Protected document' }),
    noindex: true,
  });

  useEffect(() => {
    document.body.classList.add('room-mode');
    return () => {
      document.body.classList.remove('room-mode');
      tracker.current?.stop();
      tracker.current = null;
    };
  }, []);

  // Receive reports from trackers injected into the sandboxed iframe:
  // read_progress(높이 밴드) + DeckLens 섹션 이벤트 배치.
  useEffect(() => {
    function onMessage(e: MessageEvent): void {
      if (htmlFrame.current && e.source !== htmlFrame.current.contentWindow) return;
      const data = e.data as {
        __cloudbtl?: number;
        __decklens?: number;
        type?: string;
        maxDepthPct?: number;
        bandMs?: number[];
        events?: Record<string, unknown>[];
      };
      if (!data) return;

      if (data.__cloudbtl === 1 && data.type === 'read_progress') {
        tracker.current?.enqueue('read_progress', {
          maxDepthPct: data.maxDepthPct,
          bandMs: data.bandMs,
          target: frameTarget.current,
        });
        return;
      }

      // DeckLens 배치 — RoomTracker 가 linkId·세션 신원을 붙여 /api/events 로 전달.
      if (data.__decklens === 1 && Array.isArray(data.events)) {
        for (const ev of data.events.slice(0, 50)) {
          const type = typeof ev.type === 'string' ? ev.type : '';
          if (!type) continue;
          const { type: _t, sessionId: _s, visitorId: _v, linkId: _l, ...rest } = ev;
          tracker.current?.enqueue(type, rest);
        }
      }
    }
    window.addEventListener('message', onMessage);
    return () => window.removeEventListener('message', onMessage);
  }, []);

  // srcdoc 은 iframe 마운트 후에만 주입 가능 — beginRender 시점에는 ref 가 아직
  // null 일 수 있으므로 state 로 받아 effect 에서 반영한다. sandbox 를 먼저 건다.
  useEffect(() => {
    if (frameHtml == null || !htmlFrame.current) return;
    htmlFrame.current.setAttribute('sandbox', 'allow-scripts allow-forms allow-popups');
    htmlFrame.current.srcdoc = frameHtml;
  }, [frameHtml, proposal]);

  useEffect(() => {
    if (!linkId) return;
    void load();
  }, [linkId]);

  async function load(): Promise<void> {
    setLoading(true);
    setError(null);
    try {
      const accessToken = loadAccessToken(linkId);
      const result = await api.lookupLink(linkId, accessToken || undefined);
      if (result.ok) {
        setProposal(result.proposal);
        setGate(null);
        await beginRender(result.proposal, accessToken);
      }
    } catch (err) {
      if (err instanceof ApiError) {
        const payload = (err as ApiError & { payload?: LinkLookupResponse }).payload;
        if (payload && !payload.ok && payload.accessRequired === true) {
          setGate({
            mode: payload.access.mode,
            allowedDomains: payload.access.allowedDomains,
            passcodeFallback: payload.access.passcodeFallback,
          });
        } else {
          setError(err.message);
        }
      } else {
        setError(t({ ko: '문서를 불러오지 못했습니다.', en: 'Failed to load the document.' }));
      }
    } finally {
      setLoading(false);
    }
  }

  // email 은 'email' 게이트의 자기신고 이메일 — 서버 AccessRequestDto 가 받는다.
  // (shared AccessRequest 타입에는 아직 email 이 없음 — 교차 계약, 반환 보고에 명시)
  async function unlock(body: {
    passcode?: string;
    credential?: string;
    email?: string;
  }): Promise<void> {
    try {
      const result = await api.resolveAccess(linkId, body);
      saveAccessToken(linkId, result.accessToken);
      setError(null);
      await load();
    } catch (err) {
      setError(
        err instanceof Error
          ? err.message
          : t({ ko: '접근이 허용되지 않습니다.', en: 'Access denied.' }),
      );
    }
  }

  async function submitGate(event: FormEvent): Promise<void> {
    event.preventDefault();
    await unlock({ passcode });
  }

  async function submitEmailGate(event: FormEvent): Promise<void> {
    event.preventDefault();
    await unlock({ email: gateEmail });
  }

  async function beginRender(p: Proposal, accessToken: string): Promise<void> {
    tracker.current = new RoomTracker(linkId);
    tracker.current.start();
    tracker.current.enqueue('session_start', { target: 'room' });

    const fileUrl = accessToken
      ? `${p.fileUrl}?accessToken=${encodeURIComponent(accessToken)}`
      : (p.fileUrl ?? '');
    if (!fileUrl) return;

    if (NO_PREVIEW_TYPES.has(p.documentType)) {
      // 브라우저에서 렌더할 수 없는 포맷 — pdfjs 에 넣으면 파싱 실패 화면이 되므로 다운로드 카드.
      // ★새 포맷을 업로드 허용할 때 이 집합에 넣지 않으면 수신자가 깨진 화면을 본다(2026-08-04 docx/xlsx).
      setDownloadUrl(fileUrl);
      tracker.current.enqueue('file_ready', { target: p.documentType });
    } else if (p.documentType === 'html' || p.documentType === 'md') {
      const response = await fetch(fileUrl, { credentials: 'include' });
      const text = await response.text();
      // md 는 소독 파서(renderMdBody)로만 HTML 화해 래핑 — 이후 주입·렌더 경로는 html 과 동일
      const html = p.documentType === 'md' ? wrapMdDocument(p.title, renderMdBody(text)) : text;
      const trackers =
        READ_TRACKER + decklensInjection(tracker.current.sessionId, tracker.current.visitorId);
      const injected = html.includes('</body>')
        ? html.replace('</body>', trackers + '</body>')
        : html + trackers;
      frameTarget.current = p.documentType;
      setFrameHtml(injected);
      tracker.current.enqueue('html_loaded', { target: p.documentType });
    } else {
      await renderPdf(fileUrl);
    }
  }

  async function renderPdf(url: string): Promise<void> {
    if (!pagesEl.current) return;
    pagesEl.current.innerHTML = '';
    const loadingTask = pdfjsLib.getDocument(url);
    const pdf = await loadingTask.promise;
    setTotalPages(pdf.numPages);
    setCurrentPage(1);
    tracker.current?.enqueue('pdf_loaded', { target: 'pdf', pageCount: pdf.numPages });

    const pageStarts = new Map<number, number>();
    const observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          const pageNumber = Number((entry.target as HTMLElement).dataset.pageNumber);
          if (entry.isIntersecting && entry.intersectionRatio >= 0.5) {
            setCurrentPage(pageNumber);
            if (!pageStarts.has(pageNumber)) {
              pageStarts.set(pageNumber, Date.now());
              tracker.current?.enqueue('page_enter', { pageNumber });
            }
          } else if (pageStarts.has(pageNumber)) {
            const durationMs = Date.now() - (pageStarts.get(pageNumber) ?? Date.now());
            pageStarts.delete(pageNumber);
            if (durationMs > 150) {
              tracker.current?.enqueue('page_view', { pageNumber, durationMs });
            }
          }
        }
      },
      { threshold: [0, 0.5, 0.85] },
    );

    const devicePixelRatio = Math.min(window.devicePixelRatio || 1, 2);

    for (let pageNumber = 1; pageNumber <= pdf.numPages; pageNumber += 1) {
      const page = await pdf.getPage(pageNumber);
      const containerWidth = Math.min(window.innerWidth - 24, 1200);
      const unscaled = page.getViewport({ scale: 1 });
      const scale = Math.min(2.0, Math.max(0.6, containerWidth / unscaled.width));
      const viewport = page.getViewport({ scale });
      const pageWrap = document.createElement('div');
      pageWrap.className = 'pdf-page';
      pageWrap.dataset.pageNumber = String(pageNumber);
      pageWrap.style.width = `${viewport.width}px`;
      pageWrap.style.height = `${viewport.height}px`;
      const canvas = document.createElement('canvas');
      const context = canvas.getContext('2d');
      if (!context) continue;
      canvas.width = viewport.width * devicePixelRatio;
      canvas.height = viewport.height * devicePixelRatio;
      canvas.style.width = `${viewport.width}px`;
      canvas.style.height = `${viewport.height}px`;
      context.scale(devicePixelRatio, devicePixelRatio);
      pageWrap.append(canvas);
      pagesEl.current.append(pageWrap);
      observer.observe(pageWrap);
      await page.render({ canvasContext: context, viewport }).promise;
    }
  }

  if (loading && !proposal) {
    return (
      <div className="viewer viewer-loading" role="status" aria-live="polite">
        <div className="viewer-spinner" aria-hidden="true" />
        <p>{t({ ko: '문서를 불러오는 중…', en: 'Loading document…' })}</p>
      </div>
    );
  }

  // 게이트가 살아 있으면 게이트를 먼저 — unlock 실패(오타 패스코드·형식 틀린 이메일)가
  // 전체 오류 화면으로 떨어지면 재시도 불가능해진다. 게이트 안의 인라인 error 로 표시.
  if (error && !gate) {
    return (
      <div className="viewer viewer-message">
        <article>
          <h1>{t({ ko: '문서를 사용할 수 없습니다', en: 'Document unavailable' })}</h1>
          <p>{error}</p>
        </article>
      </div>
    );
  }

  if (gate) {
    const isOrg = gate.mode === 'org';
    const isEmail = gate.mode === 'email';
    const showPasscode = gate.mode === 'passcode' || gate.passcodeFallback;
    return (
      <div className="viewer viewer-gate">
        <article>
          <h1>{t({ ko: '보호된 문서', en: 'Protected document' })}</h1>
          <p className="muted">
            {isOrg
              ? t({
                  ko: `${gate.allowedDomains.join(', ')} 소속 구성원만 이 문서를 열 수 있습니다.`,
                  en: `Only members of ${gate.allowedDomains.join(', ')} can open this document.`,
                })
              : isEmail
                ? t({
                    ko: '문서를 열려면 이메일 주소를 입력하세요.',
                    en: 'Enter your email address to open this document.',
                  })
                : t({
                    ko: '전달받은 비밀번호(패스코드)를 입력하세요.',
                    en: 'Enter the passcode shared with you.',
                  })}
          </p>

          {isEmail ? (
            <form onSubmit={submitEmailGate} data-testid="email-gate-form">
              <label>
                <span>{t({ ko: '이메일', en: 'Email' })}</span>
                <input
                  type="email"
                  required
                  autoFocus
                  autoComplete="email"
                  value={gateEmail}
                  onChange={(e) => setGateEmail(e.target.value)}
                />
              </label>
              {/* 수집 고지 — email 게이트의 목적이 신원 캡처(검증 없음)라서 이 사실을 숨기면 안 된다 */}
              <p
                className="muted"
                data-testid="email-gate-disclosure"
                style={{ margin: 0, fontSize: 12 }}
              >
                {t({
                  ko: '입력한 이메일 주소는 문서 발신자에게 전달되며, 열람 기록과 함께 표시됩니다.',
                  en: 'The email you enter is shared with the document sender and shown alongside your viewing activity.',
                })}
              </p>
              <button className="cta-primary" type="submit">
                {t({ ko: '문서 열기', en: 'Open document' })}
              </button>
            </form>
          ) : null}

          {isOrg ? (
            GOOGLE_CLIENT_ID ? (
              <div className="gate-google">
                <GoogleButton
                  text="continue_with"
                  onCredential={(credential) => void unlock({ credential })}
                  onError={(text) => setError(text)}
                />
              </div>
            ) : (
              <p className="status err">
                {t({
                  ko: 'Google 로그인이 설정되지 않았습니다.',
                  en: 'Google sign-in is not configured.',
                })}
              </p>
            )
          ) : null}

          {isOrg && showPasscode ? (
            <div className="auth-divider">
              <span>{t({ ko: '또는 비밀번호(패스코드) 사용', en: 'or use a passcode' })}</span>
            </div>
          ) : null}

          {showPasscode ? (
            <form onSubmit={submitGate}>
              <label>
                <span>{t({ ko: '비밀번호(패스코드)', en: 'Passcode' })}</span>
                <input
                  type="password"
                  required
                  autoFocus={!isOrg}
                  value={passcode}
                  onChange={(e) => setPasscode(e.target.value)}
                />
              </label>
              <button className="cta-primary" type="submit">
                {t({ ko: '문서 열기', en: 'Open document' })}
              </button>
            </form>
          ) : null}

          {error ? <p className="status err">{error}</p> : null}
        </article>
      </div>
    );
  }

  if (!proposal) return <div className="viewer" />;

  // md 는 marked 변환 결과를 같은 srcdoc iframe 으로 태운다 — html 렌더 경로 공유
  const isHtml = proposal.documentType === 'html' || proposal.documentType === 'md';
  const { branding } = proposal;

  const viewerContent = (
    <div className={`viewer ${isHtml ? 'viewer-html' : 'viewer-pdf'}`}>
      <div className="viewer-bar" role="toolbar" aria-label={t({ ko: '문서', en: 'Document' })}>
        <span className="viewer-title" title={proposal.title}>
          {proposal.title}
        </span>
        {!isHtml && totalPages > 0 ? (
          <span className="viewer-pageinfo" aria-live="polite">
            {currentPage} / {totalPages}
          </span>
        ) : null}
        {!branding && !proposal.whiteLabel && (
          <a className="viewer-brand" href="/" target="_blank" rel="noreferrer">
            via CloudBTL
          </a>
        )}
      </div>
      {isHtml ? (
        <iframe ref={htmlFrame} className="viewer-frame" title={proposal.title} />
      ) : downloadUrl ? (
        <div className="viewer-download">
          {proposal.description ? (
            <p className="viewer-download-desc" data-testid="viewer-description">
              {proposal.description}
            </p>
          ) : (
            <p>
              {t({
                ko: '이 파일은 브라우저 미리보기를 지원하지 않습니다.',
                en: 'This file has no in-browser preview.',
              })}
            </p>
          )}
          <p className="viewer-download-name muted small">{proposal.originalFilename}</p>
          <a className="cta-primary" href={downloadUrl} download>
            {t({ ko: '파일 다운로드', en: 'Download file' })}
          </a>
        </div>
      ) : (
        <div ref={pagesEl} className="viewer-pages" />
      )}
      {/* 추적 고지 — 실제 수집 항목(RoomTracker: 시각·pageUrl·viewport·visitorId,
          READ_TRACKER/DeckLens: 페이지·구간별 체류와 읽기 깊이, 서버: IP·User-Agent)과
          일치해야 한다. 계측 항목을 바꾸면 이 문구와 /legal/privacy 를 함께 고쳐라.
          flex 하단 바(문서 영역 밖)라 렌더를 가리지 않는다. */}
      <div className="viewer-notice" data-testid="tracking-notice" style={TRACKING_NOTICE_STYLE}>
        <span>
          {t({
            ko: '이 문서의 열람 정보(열람 시각·페이지별 체류·읽은 범위·IP·기기 정보·브라우저 식별자)가 문서 발신자에게 전달됩니다.',
            en: 'Your viewing activity (time opened, time per page, read depth, IP address, device info, and a browser identifier) is shared with the document sender.',
          })}
        </span>{' '}
        <a
          href="/legal/privacy"
          target="_blank"
          rel="noreferrer"
          style={{ color: 'inherit', textDecoration: 'underline' }}
        >
          {t({ ko: '자세히 보기', en: 'Details' })}
        </a>
      </div>
    </div>
  );

  if (branding) {
    return (
      <BrandingShell
        logoUrl={branding.logoUrl ?? ''}
        brandColors={{
          primary: branding.brandColors.primary ?? '#000000',
          accent: branding.brandColors.secondary,
          bg: branding.brandColors.background,
        }}
        poweredByVisible={!proposal.whiteLabel}
        refParam={branding.id}
        srcParam={`proposal_${proposal.id}`}
      >
        {viewerContent}
      </BrandingShell>
    );
  }

  return viewerContent;
}
